import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { Box, IconButton, Paper, Popper, Stack, Typography } from '@mui/material';
import React from 'react';
import { useUser } from 'src/components/hooks/useUser';
import { usePreferedDateFormat } from 'src/util/DateFormatter';
import { DataTable } from '@pihealth/common-ui-library';
import { GridRenderCellParams } from '@mui/x-data-grid-pro';
import { GridToolbarExport } from '@mui/x-data-grid-pro';
import moment from 'moment';
interface GridCellExpandProps {
  value: string;
  width: number;
}

function isOverflown(element: Element): boolean {
  return element.scrollHeight > element.clientHeight || element.scrollWidth > element.clientWidth;
}

const GridCellExpand = React.memo(function GridCellExpand(props: GridCellExpandProps) {
  const { width, value } = props;
  const wrapper: any = React.useRef<HTMLDivElement | null>(null);
  const cellDiv = React.useRef(null);
  const cellValue: any = React.useRef(null);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [showFullCell, setShowFullCell] = React.useState(false);
  const [showPopper, setShowPopper] = React.useState(false);

  const handleMouseEnter = () => {
    const isCurrentlyOverflown = isOverflown(cellValue.current);
    setShowPopper(isCurrentlyOverflown);
    setAnchorEl(cellDiv.current);
    setShowFullCell(true);
  };

  const handleMouseLeave = () => {
    setShowFullCell(false);
  };

  React.useEffect(() => {
    if (!showFullCell) {
      return undefined;
    }

    function handleKeyDown(nativeEvent: KeyboardEvent) {
      // IE11, Edge (prior to using Bink?) use 'Esc'
      if (nativeEvent.key === 'Escape' || nativeEvent.key === 'Esc') {
        setShowFullCell(false);
      }
    }

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [setShowFullCell, showFullCell]);

  return (
    <Box
      ref={wrapper}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      sx={{
        alignItems: 'center',
        lineHeight: '24px',
        width: '100%',
        height: '100%',
        position: 'relative',
        display: 'flex'
      }}
    >
      <Box
        ref={cellDiv}
        sx={{
          height: '100%',
          width,
          display: 'block',
          position: 'absolute',
          top: 0
        }}
      />
      <Box ref={cellValue} sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {value}
      </Box>
      {showPopper && (
        <Popper open={showFullCell && anchorEl !== null} anchorEl={anchorEl} style={{ width, marginLeft: -17 }}>
          <Paper elevation={6} style={{ minHeight: wrapper.current?.offsetHeight - 3 }}>
            <Typography variant="body1" style={{ padding: 12, wordBreak: 'break-word' }}>
              {value}
            </Typography>
          </Paper>
        </Popper>
      )}
    </Box>
  );
});

function renderCellExpand(params: GridRenderCellParams<string>) {
  return <GridCellExpand value={params.value || ''} width={params.colDef.computedWidth} />;
}

export default function DataGridProtocolQALog(props: any) {
  const {
    protocolQALogPermissions,
    handelEdit,
    handleDeleteOpen,
    isLoading,
    rows,
    checkboxSelection,
    pageNumber,
    setPageNumber,
    selectionModel,
    onSelectionModelChange,
    CustomFooterButtons,
    apiRef,
    topic,
    pageSize,
    setPageSize,
    rowsCount,
    setFilterObject
  } = props;
  const { preferedDateFormat, userPreferedDateFormat } = usePreferedDateFormat();

  const formatDate = (params: any) => {
    return preferedDateFormat(params.value);
  };
  const { user, email } = useUser();
  const currentUserWithEmail = `${user} (${email})`;
  const topicOption = topic.map((item: any) => item.code_list_value);

  // for Datagrdi static columns
  const columns = [
    { field: 'qa_no', headerName: 'Question Number', minWidth: 80, flex: 1 },
    {
      field: 'study_id',
      headerName: 'Study',
      minWidth: 120,
      flex: 1,
      renderCell: renderCellExpand
    },
    {
      field: 'country',
      headerName: 'Country',
      minWidth: 80,
      flex: 1,
      renderCell: renderCellExpand
    },
    {
      field: 'site_id',
      headerName: 'Site',
      minWidth: 120,
      flex: 1,
      renderCell: renderCellExpand
    },
    {
      field: 'issued_by',
      headerName: 'Issued by',
      minWidth: 120,
      flex: 1,
      renderCell: renderCellExpand
    },
    {
      field: 'topic',
      headerName: 'Topic',
      minWidth: 180,
      flex: 1,
      type: 'singleSelect',
      valueOptions: topicOption,
      renderCell: renderCellExpand
    },
    {
      field: 'summary',
      headerName: 'Summary',
      minWidth: 140,
      flex: 1,
      renderCell: renderCellExpand
    },
    {
      field: 'description',
      headerName: 'Description',
      width: 140,
      renderCell: renderCellExpand
    },
    {
      field: 'protocol_qa_assigned_to',
      headerName: 'Question assigned to',
      width: 120,
      renderCell: renderCellExpand
    },
    {
      field: 'response',
      headerName: 'Response',
      width: 90,
      renderCell: renderCellExpand
    },
    {
      field: 'response_dt',
      headerName: 'Response date',
      type: 'date',
      valueGetter: formatDate,
      width: 90
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 90,
      type: 'singleSelect',
      valueOptions: ['Open', 'In Progress', 'Answered', 'Closed']
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      sortable: false,
      width: 80,
      print: false,
      renderCell: (params: any) => {
        const assinedUser = params.row.protocol_qa_assigned_to === null ? null : params.row.protocol_qa_assigned_to[0];
        return (
          <Stack direction="row" spacing={0}>
            {protocolQALogPermissions.update ? (
              <IconButton color="primary" onClick={() => handelEdit(params.row)} title="Edit">
                <EditIcon />
              </IconButton>
            ) : assinedUser === currentUserWithEmail ? (
              <IconButton
                color="primary"
                disabled={
                  !protocolQALogPermissions.updateResponse &&
                  !protocolQALogPermissions.updateOther &&
                  !protocolQALogPermissions?.updateStatus
                }
                onClick={() => handelEdit(params.row)}
                title="Edit"
              >
                <EditIcon />
              </IconButton>
            ) : protocolQALogPermissions.updateOther || protocolQALogPermissions?.updateStatus ? (
              <IconButton color="primary" onClick={() => handelEdit(params.row)} title="Edit">
                <EditIcon />
              </IconButton>
            ) : (
              ''
            )}

            {protocolQALogPermissions.update || protocolQALogPermissions.deleteOther ? (
              <IconButton
                color="primary"
                disabled={params.row.status === 'Closed'}
                onClick={() => handleDeleteOpen(params.row)}
                title="Delete"
              >
                <DeleteIcon />
              </IconButton>
            ) : (
              ''
            )}
          </Stack>
        );
      }
    }
  ];
  const dataTableProps: any = {
    apiRef,
    sx: {
      '& .MuiDataGrid-columnHeaderTitle': {
        whiteSpace: 'break-spaces !important',
        lineHeight: 1.3,
        '&:hover': {
          textOverflow: 'ellipsis'
        }
      }
    },
    filterMode: 'server'
  };
  const definedCustomDatGridExport = (exportFields: any = {}) => {
    return (
      <GridToolbarExport
        csvOptions={{
          fileName: `${`Protocol Q&A Log`} ${moment(new Date()).format(
            `${(userPreferedDateFormat.includes('/')
              ? userPreferedDateFormat.replaceAll('/', '_')
              : userPreferedDateFormat.replaceAll('-', '_')
            ).toUpperCase()} hh_mm A`
          )}`
        }}
      />
    );
  };
  const filterValues = (data: any) => {
    setFilterObject(data);
  };

  return (
    <>
      <DataTable
        rows={rows}
        columns={columns}
        hasDefinedColumns
        loading={isLoading}
        datePreference={userPreferedDateFormat}
        paging
        rowsCount={rowsCount}
        pageNumber={pageNumber}
        pageSize={pageSize}
        setPageSize={setPageSize}
        setPageNumber={setPageNumber}
        paginationMode="server"
        className="studylogDatagrid"
        exportAll
        height="74vh"
        disableSelectionOnClick
        checkboxSelection={checkboxSelection}
        selectionModel={selectionModel}
        onRowsSelection={onSelectionModelChange}
        CustomFooter={CustomFooterButtons}
        checkboxSelectionVisibleOnly
        dataTableProps={dataTableProps}
        pinnedColumns={{
          right: ['actions']
        }}
        hasDefinedDataGridToolbarExport
        definedGridToolbarExport={definedCustomDatGridExport}
        filterValues={(data: any) => filterValues(data)}
      />
    </>
  );
}
